/* ═══════════════════════════════════════════════════════════════
   Ähnliche Jahrgänge einer Region — ähnlich in der WITTERUNG

   Die Oberfläche sagt „Jahrgänge mit ähnlicher Witterung“, nicht
   „ähnliche Jahrgänge“: Ob Kenner diese Jahre auch im Glas für ähnlich
   halten, ist an 45 belegten Urteilen nur schwach belegt (8,5 % Treffer
   gegen 3,5 % Zufall und 6,1 % für die drei Nachbarjahre, 28.10.2026).

   „Wie war 2018 hier?" lässt sich schwer beantworten, „2018 war wie
   2003 und 2015" dagegen sofort einordnen — wer diese Jahrgänge im
   Glas hatte, weiß, was ihn erwartet.

   Grundlage ist die Klimareihe (`klimareihe()` in wetter.js). Sie
   liefert für JEDES Jahr seit 1950 die Kennzahlen der Vegetations-
   periode, und zwar aus einem einzigen Abruf. Für diese Funktion muss
   also nichts zusätzlich geladen werden.

   Verglichen wird über vier Größen, die den Charakter eines Jahrgangs
   prägen:

     tMittel   Durchschnittstemperatur — Reife, Alkohol, Säure
     gdd       Wärmesumme — dasselbe, aber über die Zeit integriert
     regen     Niederschlag — Verdünnung, Fäulnisdruck, Stress
     sonne     Sonnenstunden — Aromenreife, Farbe

   `tMax`, `hitzetage` und `frosttage` bleiben bewusst draußen: Sie
   korrelieren stark mit den vier obigen und würden die Temperatur
   dreifach zählen.

   Jede Größe wird an der Streuung der eigenen Reihe gemessen
   (z-Wert), nicht an ihrer absoluten Höhe. Sonst dominierte der
   Niederschlag mit seinen Hunderten Millimetern jeden Vergleich,
   während zwei Grad Temperaturunterschied — für den Wein gravierend —
   unterginge.
   ═══════════════════════════════════════════════════════════════ */

/* ── Woran ein Jahrgang gemessen wird ──────────────────────────

   Die Fachliteratur ist sich einig, dass drei Achsen den Charakter
   eines Weinjahrs bestimmen. Tonietto & Carbonneau haben sie 2004 in
   der Géoviticulture-Klassifikation zusammengefasst (von der WMO
   getragen); ihre Hauptkomponentenanalyse führt über 90 % der
   klimatischen Streuung auf diese drei zurück:

     Wärme      Huglin-Index bzw. Gradtage — Reife, Zucker, Alkohol
     Wasser     Trockenheitsindex — Verdünnung, Stress, Fäulnisdruck
     Nächte     Kühle-Nächte-Index — Farbe, Aroma, Polyphenole

   Danach ist dieses Modell gebaut. Die erste Fassung hatte nur
   Temperatur, Gradtage, Regen und Sonne — sie zählte die Wärme
   doppelt und ließ die Nächte ganz aus.

   Zwei Abweichungen von der reinen Lehre, beide bewusst:

   - Statt des Huglin-Index werden Mitteltemperatur UND Gradtage
     benutzt, jede mit halbem Gewicht. Beide beschreiben die Wärme,
     aber unterschiedlich: das Mittel die Höhe, die Gradtage die
     Dauer. Huglin verlangt einen Breitengrad-Korrekturfaktor, der
     beim Vergleich INNERHALB einer Region ohnehin herausfällt.
   - Statt des Trockenheitsindex (der eine Bodenwasserbilanz und
     Verdunstung braucht) wird der Niederschlag genommen, getrennt
     nach Gesamtperiode und Reifephase. Regen im September ist etwas
     völlig anderes als Regen im Mai — genau das war der Unterschied
     zwischen 2006 (heißer Juli, nasser August) und einem gleichmäßig
     warmen Jahr.
   ────────────────────────────────────────────────────────────── */

/* ── Reifebezug (ab Klimareihe Fassung 4) ──────────────────────
   Die erste Fassung verglich alles am Kalender: Saisonsummen von
   April bis Oktober, Monatskurven Monat für Monat. Ein Hitzejahr, das
   Ende August reif war, und ein kühles, das bis Ende Oktober hing,
   wurden so über denselben September verglichen — beim einen lag er
   nach der Lese, beim anderen mitten in der Reife.

   Jetzt ist der geschätzte Reifetermin (`reifephasen` in wetter.js)
   der Bezug:
   - Frühreife (`leseTag`) zählt als dritte Wärmegröße. „Ein frühes
     Jahr“ ist das, woran man Jahrgänge zuerst einordnet, und es fasst
     zusammen, WANN die Wärme kam — die Mittelwerte sagen nur, wie viel.
   - Regen und Sonne werden bis zur Reife gezählt (`regenLese`,
     `sonneLese`), nicht bis Ende Oktober.
   - Der Verlauf vergleicht sechs Phasen gleicher Wärme statt sieben
     Kalendermonate (siehe `verlaufsQuelle`).
   Reiferegen und Nächte hängen schon seit Fassung 3 am Termin.

   `optionen.kalender` schaltet die alte Rechnung ein — nur für die
   Kalibrierung, damit beide Fassungen an echten Reihen verglichen
   werden können (`jahrgangs-kalibrierung.js --kalender`). */

/* ── Abweichung vom Üblichen (ab Klimareihe Fassung 5) ─────────────
   Fassung 4 maß Nächte, Regen und Sonne absolut am Reifetermin. Damit
   maßen alle drei den Termin mit: Ein früh reifes Jahr hat seine
   Nächte im August (milder), sammelt bis zur Reife weniger Regen und
   weniger Sonnenstunden. An einer erzeugten Mosel-Reihe lag
   `reifeNacht` bei r = −0,95 zu `leseTag`; die Achse „Nächte“ war eine
   zweite Wärmeachse, und die Wärme zählte so fast doppelt. Jetzt gilt
   die Abweichung von der Normalen DERSELBEN Tage (`nachtAbw`,
   `regenAbw`, `sonneAbw` aus `reifephasen` in wetter.js): Frühreife
   steckt in der Wärme, die übrigen Achsen sagen, was das Wetter
   darüber hinaus war. Die drei Achsen sind damit näher an dem, was
   Tonietto & Carbonneau meinen — weitgehend unabhängig.

   GEMESSEN — zweimal, und erst die zweite Messung zählt:

   26.10.2026 an „Best Match“-Reihen (elf Regionen, 25 Winzerurteile,
   je eine Region ausgelassen): Fassung 4 9,7 %, Fassung 5 7,6 %,
   Kalender 9,0 %. Daraufhin rechnete kurz wieder Fassung 4. Diese
   Reihen waren aber 2017 gebrochen (Modellwechsel, siehe MODELL in
   wetter.js) — gerade bei Regen und Nächten, also den Achsen, in
   denen sich 4 und 5 unterscheiden.

   27.10.2026 an gleichmäßigen Reihen (ERA5-Seamless, Fassung 6):

                    ausgelassen        eingebaute Gewichte
     Fassung 4      11,8 % (p 0,013)    9,0 % (p 0,056)
     Fassung 5      16,0 % (p 0,001)   11,8 % (p 0,013)
     Kalender       18,8 % (p <0,001)   5,6 % (p 0,25)
     Zufall 3,5 %, drei zeitlich nächste Jahre 5,6 %

   Fassung 5 liegt vor Fassung 4, mit beiden Gewichten. Fall für Fall
   ist der Abstand nicht gesichert (Vorzeichentest p = 0,75) — die
   Urteile trennen die beiden nicht; den Ausschlag geben die gemessene
   Störgröße (Nächte absolut r ≈ −0,92 zum Termin) und dass beide
   Messungen an gleichmäßigen Reihen in dieselbe Richtung zeigen.
   Der Kalender liegt mit angepassten Gewichten vorn, mit den
   eingebauten auf Höhe der Zeitnachbarn — er hängt ganz an der
   Gewichtung; Fall für Fall ist auch er nicht zu trennen (Gewinne in
   Bordeaux und Burgund, Verluste in Wachau und Mendoza). Daraufhin
   rechnete Fassung 5 — bis zur Prüfung an Satz 2 (unten).

   GEWICHTE — angepasst und wieder zurückgenommen. Am 27.10. wählten 9
   von 10 Faltungen W1 H2O0,7 N1,3 V0,3 (ausgelassen 16 %), und diese
   Gewichte wurden eingebaut. Die Prüfung an 20 UNABHÄNGIGEN Urteilen
   (Satz 2, 28.10.2026, Erwartung vorher festgelegt: ≈ 22–24 Punkte,
   über dem Zufall ab 15) ergab 2 von 149 Punkten — weniger als drei
   zufällige Jahre (3,6 %). Die Anpassung hatte sich an Satz 1 gewöhnt.
   Auch die einzelnen Achsen kippen zwischen den Sätzen: Wärme allein
   15,3 % an Satz 1, 0,7 % an Satz 2; Wasser allein 4 % bzw. 7–13 %.
   Mit 20 bis 25 Urteilen je Satz lassen sich Gewichte nicht messen.
   Deshalb wieder die Vorab-Gewichte: je Achse 1,0, Verlauf 0,15 —
   gewählt aus der Literatur, nie an Urteilen angepasst. Fassung 4 mit
   ihren (ebenso unangepassten) Vorab-Gewichten traf an beiden Sätzen
   gleich viel: 9,0 % und 8,1 %, zusammen 25 von 293 Punkten (p 0,024
   gegen Zufall, Zeitnachbarn 18).

   WELCHE FASSUNG RECHNET — vorab festgelegt, dann gemessen (28.10.2026):
   Fassung 5 bleibt, wenn sie mit Vorab-Gewichten über beide Sätze
   mindestens so viele Punkte holt wie Fassung 4. Ergebnis: Fassung 5
   17 + 4 = 21 von 293, Fassung 4 13 + 12 = 25, Kalender 14. Fall für
   Fall nicht zu trennen (p 0,67) — aber die Regel stand vorher fest.
   Fassung 4 rechnet; Fassung 5 bleibt über `optionen.merkmale =
   "fassung5"` erreichbar, ihre Felder bleiben in der Reihe. Die
   Begründung für Fassung 5 (Nächte absolut hängen am Termin) gilt
   weiter fürs Stilprofil, das die Abweichungen nutzt — für die
   Ähnlichkeit hat sie sich an den Urteilen nicht ausgezahlt. */
const MERKMALE = [
  /* Wärme — zusammen 1,0 */
  { feld: "tMittel", gewicht: 0.35, gruppe: "waerme" },
  { feld: "gdd", gewicht: 0.35, gruppe: "waerme" },
  { feld: "leseTag", gewicht: 0.3, gruppe: "waerme" },
  /* Wasser — zusammen 1,0, Schwerpunkt auf der Reifephase.
     `reifeRegen` bleibt absolut: Für Fäulnis zählt, was fiel. */
  { feld: "regenAbw", gewicht: 0.4, gruppe: "wasser" },
  { feld: "reifeRegen", gewicht: 0.6, gruppe: "wasser" },
  /* Nächte und Licht — zusammen 1,0 */
  { feld: "nachtAbw", gewicht: 0.6, gruppe: "naechte" },
  { feld: "sonneAbw", gewicht: 0.4, gruppe: "naechte" },
];

/* Fassung 4 — absolut am Reifetermin. Rechnet seit 28.10.2026 (siehe
   „WELCHE FASSUNG RECHNET“ oben); `MERKMALE` (Fassung 5) nur auf
   Wunsch über `optionen.merkmale = "fassung5"`. */
const MERKMALE_F4 = [
  { feld: "tMittel", gewicht: 0.35, gruppe: "waerme" },
  { feld: "gdd", gewicht: 0.35, gruppe: "waerme" },
  { feld: "leseTag", gewicht: 0.3, gruppe: "waerme" },
  { feld: "regenLese", gewicht: 0.4, gruppe: "wasser" },
  { feld: "reifeRegen", gewicht: 0.6, gruppe: "wasser" },
  { feld: "reifeNacht", gewicht: 0.6, gruppe: "naechte" },
  { feld: "sonneLese", gewicht: 0.4, gruppe: "naechte" },
];

/* Die neueste Fassung der Klimareihe, die dieses Modul kennt — muss
   `FASSUNG` in wetter.js folgen (tests/jahrgangsmodell.test.mjs prüft
   das). Ältere Reihen rechnen weiter, die Oberfläche rät aber zum
   Aktualisieren. Ab 6 ist die Reihe ohne Modellbruch 2017 (MODELL in
   wetter.js) — ältere können in den Bergen um Grade springen. Ab 7
   trägt sie `abrufBis` und `reifeRegenGeschaetzt` (siehe
   `reiheVorlaeufig`). */
const KLIMA_FASSUNG = 7;

/** Ist die Klimareihe älter als die Rechnung? Dann fehlen Felder, und
    Vergleich wie Stilprofil laufen auf einer älteren Fassung. */
function reiheVeraltet(klima) {
  return !!(klima && Array.isArray(klima.jahre) && klima.jahre.length > 0
    && (klima.fassung || 0) < KLIMA_FASSUNG);
}

/* ═══════════════════════════════════════════════════════════════
   WANN EIN JAHRGANG ABGESCHLOSSEN IST

   Nicht zum Jahreswechsel, sondern sobald sein Fenster im Archiv
   liegt: Fensterende (31.10. bzw. 30.04.) plus fünf Tage Verzug von
   ERA5 (open-meteo.com: „Daily with 5 days delay“). Ab dann nimmt der
   Server das Jahr in die Klimareihe auf — im Norden ab dem 6.
   November. Gegenstück zu `reihenZeitraum` in wetter.js, beide
   rechnen mit derselben Grenze (tests/jahrgangsfrueh.test.mjs).

   Die Reihe liegt ohne Verfallsdatum im Kellerbuch. Eine im Oktober
   geladene enthielt das Jahr deshalb nie, und das Panel öffnete bis
   Silvester auf dem Vorjahr — Vergleich und Stilprofil wirkten, als
   gäbe es sie erst nach Jahresende.
   ═══════════════════════════════════════════════════════════════ */
const ARCHIV_VERZUG_TAGE = 6;
const NACHLAUF_TAGE = 20;   /* REIFE.regenNach in wetter.js */

const fensterEnde = (sued, jahr) => (sued ? new Date(jahr, 3, 30) : new Date(jahr, 9, 31));

/** Jüngster Jahrgang, dessen Vegetationsperiode vollständig im Archiv
    liegt. */
function letztesAbgeschlossenesJahr(sued, heute = new Date()) {
  const grenze = new Date(heute);
  grenze.setDate(grenze.getDate() - ARCHIV_VERZUG_TAGE);
  let jahr = grenze.getFullYear();
  if (fensterEnde(sued, jahr) > grenze) jahr -= 1;
  return jahr;
}

/** Abgeschlossener Jahrgang, der in der geladenen Reihe noch fehlt —
    oder `null`. */
function fehlendesJahr(klima, heute = new Date()) {
  if (!klima || !Array.isArray(klima.jahre) || klima.jahre.length === 0) return null;
  const letztes = klima.bis != null ? klima.bis : klima.jahre[klima.jahre.length - 1].jahr;
  const soll = letztesAbgeschlossenesJahr(!!klima.sued, heute);
  return letztes < soll ? soll : null;
}

/** Ist der Reiferegen dieses Jahres nur hochgerechnet, weil die Tage
    nach der Lese beim Laden der Reihe noch nicht im Archiv lagen?
    Dann `{ ab }`: der Tag (ISO), ab dem eine neu geladene Reihe sie
    sicher enthält — Fensterende plus Nachlauf plus Verzug, dieselbe
    Grenze, ab der der Server seine vorläufige Reihe verwirft
    (`reiheNochGut`). Lücken in alten Jahren zählen nicht: Nur wenn
    die Reihe VOR diesem Tag geladen wurde. */
function reiheVorlaeufig(klima, jahr) {
  if (!klima || !Array.isArray(klima.jahre)) return null;
  const e = klima.jahre.find((j) => j.jahr === jahr);
  if (!e || !e.reifeRegenGeschaetzt) return null;
  const ab = fensterEnde(!!klima.sued, jahr);
  ab.setDate(ab.getDate() + NACHLAUF_TAGE + ARCHIV_VERZUG_TAGE);
  if (klima.geladen && klima.geladen >= ab.getTime()) return null;
  const iso = `${ab.getFullYear()}-${String(ab.getMonth() + 1).padStart(2, "0")}-${String(ab.getDate()).padStart(2, "0")}`;
  return { ab: iso, abMs: ab.getTime() };
}

/* Die Merkmale bis Fassung 3 — am Kalender, für `optionen.kalender`. */
const MERKMALE_KALENDER = [
  { feld: "tMittel", gewicht: 0.5, gruppe: "waerme" },
  { feld: "gdd", gewicht: 0.5, gruppe: "waerme" },
  { feld: "regen", gewicht: 0.4, gruppe: "wasser" },
  { feld: "reifeRegen", gewicht: 0.6, gruppe: "wasser" },
  { feld: "reifeNacht", gewicht: 0.6, gruppe: "naechte" },
  { feld: "sonne", gewicht: 0.4, gruppe: "naechte" },
];

/* Für die Oberfläche („1,2 °C wärmer“, „12 Tage früher reif“) —
   unabhängig davon, welche Merkmale gerade gerechnet werden. */
const UNTERSCHIED_FELDER = ["tMittel", "gdd", "regen", "sonne", "leseTag",
  "regenLese", "sonneLese", "reifeRegen", "reifeNacht", "nachtAbw", "regenAbw", "sonneAbw"];

/** Welche Merkmale gerechnet werden: Fassung 4, sofern jedes Jahr
    einen Reifetermin trägt, sonst der Kalender (siehe oben, gemessen
    28.10.2026). Fassung 5 und der Kalender lassen sich über `optionen`
    erzwingen — Fassung 5 nur, wenn JEDES Jahr die Abweichungen trägt.
    Gemischt wird nie: Ein Jahr mit Abweichung neben einem mit
    Absolutwert wäre kein Vergleich. */
function merkmalsFassung(klima, optionen) {
  const gewuenscht = optionen && (optionen.kalender ? "kalender" : optionen.merkmale);
  const jedes = (...felder) => klima.jahre.length > 0
    && klima.jahre.every((j) => felder.every((f) => zahl(j[f]) !== null));
  if (gewuenscht === "kalender" || !jedes("leseTag")) return "kalender";
  if (gewuenscht === "fassung5" && jedes("nachtAbw", "regenAbw", "sonneAbw")) return "fassung5";
  return "fassung4";
}

const MERKMALE_JE_FASSUNG = { fassung5: MERKMALE, fassung4: MERKMALE_F4, kalender: MERKMALE_KALENDER };

/* Der Verlauf ist eine vierte Achse, aber eine leise.

   Das Gewicht ist nicht geschätzt, sondern gemessen: An vier belegten
   Jahrgangsurteilen (Mosel, siehe Kommentar bei `aehnlicheJahrgaenge`)
   wurde 0 / 0,15 / 0,25 / 0,35 / 0,5 / 0,75 durchprobiert und die
   Trefferlage bewertet.

     0      18 Punkte   (Verlauf aus)
     0,15   19 Punkte   ← Optimum
     0,25   18 Punkte
     0,35+  16 Punkte

   Über 0,25 kippt es: Die Form verdrängt dann die Kennzahlen, und
   2018 verliert 2003 als nächsten Nachbarn. Der Verlauf trägt also
   etwas bei — aber wenig, und wer ihn hoch hängt, macht es
   schlechter. Genau deshalb wurde er gemessen und nicht geraten.

   NACHTRAG 28.10.2026: Am 27.10. auf 0,3 gesetzt (9 von 10 Faltungen
   an 25 Urteilen), an unabhängigen Urteilen nicht bestätigt (siehe
   MERKMALE) — wieder 0,15 für alle Fassungen. Die Tabelle oben stammt
   ebenfalls aus nur vier Fällen; belegt ist das Gewicht damit nicht,
   nur nicht widerlegt. */
const VERLAUF_JE_FASSUNG = { fassung5: 0.15, fassung4: 0.15, kalender: 0.15 };
const VERLAUF_GEWICHT = VERLAUF_JE_FASSUNG.fassung4;
/* Die drei Achsen zusammen — Bezugsgröße beim Mischen. */
const GRUPPEN_GEWICHT = 1;

/* Wie viele Monate am Ende der Vegetationsperiode als Reifephase
   gelten. Auf der Nordhalbkugel sind das September und Oktober —
   der Zeitraum, den die Literatur für den Kühle-Nächte-Index und
   den Reifeniederschlag heranzieht. */
const REIFEMONATE = 2;


/* ── Der Verlauf innerhalb der Saison ──────────────────────────

   Zwei Jahre mit denselben Saisonsummen können völlig verschiedene
   Weine ergeben. 2006 an der Mosel ist das Lehrbuchbeispiel: ein
   Juli, heißer als 2003, gefolgt von einem viel zu nassen August —
   die Beerenhäute wurden dünn und platzten beim nächsten Regen. In
   der Jahressumme sieht das unauffällig aus.

   Verglichen wird deshalb zusätzlich die FORM der Monatskurve. Von
   jedem Monat wird das Jahresmittel abgezogen; übrig bleibt, wie
   sich der Sommer verteilt hat — die Höhe steckt schon in `tMittel`
   und `regen` und würde sonst ein drittes Mal zählen.

   Die Abweichung je Monat wird an ihrer eigenen Streuung über alle
   Jahre gemessen: Ein warmer April schwankt anders als ein warmer
   August. */

/** Monatliche Abweichung vom Jahresmittel — die Form der Kurve. */
function verlaufsForm(monate, feld) {
  if (!Array.isArray(monate) || monate.length === 0) return null;
  const werte = monate.map((m) => zahl(m && m[feld]));
  const da = werte.filter((v) => v !== null);
  if (da.length < monate.length) return null;   /* Lücken: keine Form */
  const mittel = da.reduce((a, b) => a + b, 0) / da.length;
  return werte.map((v) => v - mittel);
}


/** Streuung je Monatsposition, über alle Jahre. */
function formStreuungen(jahre, monateJeJahr, feld) {
  const spalten = [];
  for (const { jahr } of jahre) {
    const form = verlaufsForm(monateJeJahr && monateJeJahr[jahr], feld);
    if (!form) continue;
    form.forEach((v, i) => { (spalten[i] || (spalten[i] = [])).push(v); });
  }
  return spalten.map((werte) => {
    if (!werte || werte.length < 2) return null;
    const m = werte.reduce((a, b) => a + b, 0) / werte.length;
    const sigma = Math.sqrt(werte.reduce((s, v) => s + (v - m) ** 2, 0) / werte.length);
    return sigma > 0 ? sigma : null;
  });
}


/** Abstand zweier Kurvenformen, normiert je Monat. `null`, wenn
    einer der beiden keine vollständige Kurve hat. */
function formAbstand(formA, formB, sigmaSpalten) {
  if (!formA || !formB || formA.length !== formB.length) return null;
  let summe = 0, n = 0;
  for (let i = 0; i < formA.length; i++) {
    const s = sigmaSpalten[i];
    if (!s) continue;
    const d = (formA[i] - formB[i]) / s;
    summe += d * d; n += 1;
  }
  return n === 0 ? null : Math.sqrt(summe / n);
}


/** Ergänzt einen Jahrgang um die Kennzahlen der Reifephase.
    Fehlt der Monatsverlauf (ältere Klimareihe), bleiben sie null und
    werden beim Vergleich übersprungen. */
/** Kühle-Nächte-Index nach Tonietto & Carbonneau (2004): mittlere
    Tiefsttemperatur im Monat VOR der Lese — September auf der Nord-,
    März auf der Südhalbkugel, im Vegetationsfenster der vorletzte
    Monat.

    Bis hierher wurden September und Oktober gemittelt. Das verfälschte
    Hitzejahre mit kaltem Oktober — 2003 an der Mosel —, obwohl der
    Oktober dort großteils nach der Lese liegt. Stilprofil und Vergleich
    teilen sich jetzt diese eine Definition. */
function kuehleNaechte(monateJeJahr, jahr) {
  const monate = monateJeJahr && monateJeJahr[jahr];
  if (!Array.isArray(monate) || monate.length < 2) return null;
  const m = monate[monate.length - 2];
  return m && typeof m.tMin === "number" && Number.isFinite(m.tMin) ? m.tMin : null;
}

function mitReifephase(j, monateJeJahr, kalender = false) {
  /* Ab Klimareihe Fassung 3 rechnet der Server Regen und Nächte am
     geschätzten Lesetermin aus Tageswerten (`reifephasen` in
     wetter.js) — ein heißes Jahr reift im August, dann zählt der
     Oktoberregen nicht mehr. Die Monatsrechnung unten ist nur noch der
     Rückfall für ältere, zwischengespeicherte Reihen. */
  if (!kalender && zahl(j.reifeRegen) !== null && zahl(j.reifeNacht) !== null) {
    /* Fassung 3 trug noch keine Summen bis zur Reife — dann gelten
       die Saisonsummen, wie vorher. */
    return {
      ...j,
      regenLese: zahl(j.regenLese) ?? zahl(j.regen),
      sonneLese: zahl(j.sonneLese) ?? zahl(j.sonne),
    };
  }
  /* Rückfall: Ohne Reifetermin sind die Summen bis zur Reife die
     Saisonsummen, und die Frühreife bleibt leer (wird übersprungen). */
  const ohneTermin = { ...j, regenLese: zahl(j.regen), sonneLese: zahl(j.sonne), leseTag: null };
  const monate = monateJeJahr && monateJeJahr[j.jahr];
  if (!Array.isArray(monate) || monate.length === 0) return ohneTermin;
  /* Der Regen bleibt über beide Reifemonate: Fäulnisdruck entsteht
     auch noch kurz vor der Lese, und die Lese selbst reicht in den
     Oktober. Nur die Nächte sind an den einen Monat gebunden. */
  const letzte = monate.slice(-REIFEMONATE);
  const regenWerte = letzte.map((m) => m && m.regen).filter((v) => typeof v === "number");
  return {
    ...ohneTermin,
    reifeRegen: regenWerte.length ? regenWerte.reduce((a, b) => a + b, 0) : null,
    reifeNacht: kuehleNaechte(monateJeJahr, j.jahr),
  };
}

const zahl = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);


/** Streuung je Merkmal über die ganze Reihe. */
function streuungen(jahre, merkmale = MERKMALE) {
  const raus = {};
  for (const { feld } of merkmale) {
    const werte = jahre.map((j) => zahl(j[feld])).filter((v) => v !== null);
    if (werte.length < 2) { raus[feld] = null; continue; }
    const mittel = werte.reduce((a, b) => a + b, 0) / werte.length;
    const varianz = werte.reduce((s, v) => s + (v - mittel) ** 2, 0) / werte.length;
    const sigma = Math.sqrt(varianz);
    /* Eine Reihe ohne Streuung trägt nichts zur Unterscheidung bei. */
    raus[feld] = sigma > 0 ? sigma : null;
  }
  return raus;
}


/**
 * Abstand zweier Jahrgänge, 0 = deckungsgleich.
 *
 * Merkmale, die bei einem der beiden fehlen, werden übersprungen —
 * das Ergebnis wird auf die tatsächlich verglichenen normiert, damit
 * ein Jahr mit Lücken nicht künstlich ähnlich wirkt.
 * Gibt `null` zurück, wenn gar nichts vergleichbar ist.
 */
function abstand(a, b, sigma, merkmale = MERKMALE) {
  let summe = 0, gewichte = 0;
  for (const { feld, gewicht } of merkmale) {
    const s = sigma[feld];
    const va = zahl(a[feld]), vb = zahl(b[feld]);
    if (s == null || va === null || vb === null) continue;
    const d = (va - vb) / s;
    summe += gewicht * d * d;
    gewichte += gewicht;
  }
  if (gewichte === 0) return null;
  return Math.sqrt(summe / gewichte);
}


/** Woran der Verlauf gemessen wird: an den sechs Phasen gleicher
    Wärme bis zur Reife, wenn JEDES Jahr sie trägt (Klimareihe ab
    Fassung 4), sonst an den Kalendermonaten. Gemischt wird nie — eine
    Phasenkurve neben einer Monatskurve wäre kein Vergleich. */
function verlaufsQuelle(klima, kalender) {
  if (!kalender && klima.jahre.length > 0
    && klima.jahre.every((j) => Array.isArray(j.phasen) && j.phasen.length > 0)) {
    return { art: "phasen", jeJahr: Object.fromEntries(klima.jahre.map((j) => [j.jahr, j.phasen])) };
  }
  return { art: "kalender", jeJahr: klima.monateJeJahr || null };
}


/**
 * Die ähnlichsten Jahrgänge zu einem gegebenen Jahr.
 *
 * @param klima   Ergebnis von `klimareihe()` — braucht `jahre`.
 * @param jahr    Bezugsjahrgang.
 * @param anzahl  Wie viele zurückgegeben werden (Vorgabe 3).
 * @returns  Liste `{ jahr, abstand, werte, unterschiede }`, die
 *           ähnlichsten zuerst. Leer, wenn kein Vergleich möglich ist.
 *
 * `unterschiede` nennt je Merkmal die Abweichung in der Einheit der
 * Größe — damit die Oberfläche „1,2 °C wärmer" schreiben kann statt
 * einer nackten Kennzahl, die niemandem etwas sagt.
 */
function aehnlicheJahrgaenge(klima, jahr, anzahl = 3, optionen = null) {
  /* Gewichte sind übersteuerbar — nicht für den Betrieb, sondern für
     die Kreuzvalidierung (jahrgangs-kalibrierung.js). Ohne diesen
     Weg müsste sie für jede Faltung den Quelltext umschreiben. */
  const gruppen = (optionen && optionen.gruppen) || null;
  if (!klima || !Array.isArray(klima.jahre)) return [];
  if (!klima.jahre.some((j) => j.jahr === jahr)) return [];
  /* Ohne Reifetermin in der Reihe (vor Fassung 4) gilt die alte
     Rechnung vollständig. Nur die Frühreife wegzulassen hieße, der
     Wärme still ein Drittel ihres Gewichts zu nehmen. Ebenso ohne die
     Abweichungen (vor Fassung 5): dann Fassung 4 vollständig. */
  const fassung = merkmalsFassung(klima, optionen);
  const verlaufGewicht = (optionen && optionen.verlaufGewicht != null)
    ? optionen.verlaufGewicht : VERLAUF_JE_FASSUNG[fassung];
  const alteRechnung = !!(optionen && (optionen.kalender || optionen.merkmale === "kalender"));
  const kalender = fassung === "kalender";
  const basis = MERKMALE_JE_FASSUNG[fassung];
  const merkmale = gruppen
    ? basis.map((m) => ({ ...m, gewicht: m.gewicht * (gruppen[m.gruppe] ?? 1) }))
    : basis;

  /* Reifephase aus dem Monatsverlauf ergänzen — für alle Jahre, damit
     auch die Streuung darüber gebildet wird. */
  const jahre = klima.jahre.map((j) => mitReifephase(j, klima.monateJeJahr, alteRechnung));
  const sigma = streuungen(jahre, merkmale);
  const bezug = jahre.find((j) => j.jahr === jahr);

  /* Kurvenformen für Temperatur und Regen, je Abschnitt normiert —
     Phasen gleicher Wärme oder, bei älteren Reihen, Kalendermonate.
     Einmal je Jahr, weil die Einstufung alle Paare braucht. */
  const quelle = verlaufsQuelle(klima, kalender);
  const mjj = quelle.jeJahr;
  const sigTemp = formStreuungen(jahre, mjj, "temp");
  const sigRegen = formStreuungen(jahre, mjj, "regen");
  const formen = new Map(jahre.map((j) => [j.jahr, {
    t: verlaufsForm(mjj && mjj[j.jahr], "temp"),
    r: verlaufsForm(mjj && mjj[j.jahr], "regen"),
  }]));

  /* Abstand zweier Jahre: Kennzahlen, dazu der Verlauf, sofern beide
     Kurven vollständig sind. Fehlt der Monatsverlauf (ältere
     Klimareihe), bleibt es beim Abstand über die Kennzahlen — kein
     Grund, gar nichts zu zeigen. */
  const vergleiche = (a, b) => {
    const d = abstand(a, b, sigma, merkmale);
    if (d === null) return null;
    const fa = formen.get(a.jahr), fb = formen.get(b.jahr);
    const teile = [formAbstand(fa.t, fb.t, sigTemp), formAbstand(fa.r, fb.r, sigRegen)]
      .filter((x) => x !== null);
    const verlauf = teile.length ? Math.sqrt(teile.reduce((s, x) => s + x * x, 0) / teile.length) : null;
    const gesamt = verlauf === null
      ? d
      : Math.sqrt((d * d * GRUPPEN_GEWICHT + verlauf * verlauf * verlaufGewicht) / (GRUPPEN_GEWICHT + verlaufGewicht));
    return { gesamt, d, verlauf };
  };

  /* Einstufung an der Verteilung ALLER Jahrespaare der Region. Bis
     hierher waren es nur die Paare zum Bezugsjahr — damit lagen dessen
     drei nächste Nachbarn bei 76 Jahren immer unter dem 5-%-Quantil,
     und jeder angezeigte Treffer hieß „sehr ähnlich“ (an einer
     erzeugten Mosel-Reihe 228 von 228). Gemessen an allen Paaren sagt
     die Stufe, ob es zu DIESEM Jahr ein echtes Gegenstück gibt: Ein
     gewöhnliches Jahr hat sehr ähnliche Nachbarn, ein Ausreißer wie
     2003 vielleicht nur „gut vergleichbare“. Für die Kalibrierung
     abschaltbar (`stufen: false`) — sie braucht nur die Reihenfolge. */
  let schwellen = null;
  const mitStufen = !(optionen && optionen.stufen === false);
  if (mitStufen) {
    const alle = [];
    for (let i = 0; i < jahre.length; i++) {
      for (let k = i + 1; k < jahre.length; k++) {
        const v = vergleiche(jahre[i], jahre[k]);
        if (v) alle.push(v.gesamt);
      }
    }
    schwellen = schwellenAus(alle);
  }

  /* Regen und Sonne bis zur Reife für die Anzeige: Die Abweichung je
     Tag mal der üblichen Dauer bis zur Reife — „40 mm nasser“ heißt
     dann: in einer gewöhnlich langen Reifezeit 40 mm mehr als im
     Bezugsjahr. Die Summen bis zur Reife wären durch die Dauer
     verzerrt: Ein spätes, trübes Jahr hieße „sonniger“. */
  const dauern = jahre.map((j) => zahl(j.leseTag)).filter((v) => v !== null).map((v) => v + 1);
  const typischeTage = dauern.length ? median(dauern) : null;

  const raus = [];
  for (const kandidat of jahre) {
    if (kandidat.jahr === jahr) continue;
    const v = vergleiche(bezug, kandidat);
    if (v === null) continue;
    const unterschiede = {};
    for (const feld of UNTERSCHIED_FELDER) {
      const va = zahl(bezug[feld]), vb = zahl(kandidat[feld]);
      unterschiede[feld] = (va === null || vb === null) ? null : +(vb - va).toFixed(STELLEN[feld] ?? 1);
    }
    for (const [feld, roh] of [["regenBisReife", "regenAbw"], ["sonneBisReife", "sonneAbw"]]) {
      const va = zahl(bezug[roh]), vb = zahl(kandidat[roh]);
      unterschiede[feld] = (va === null || vb === null || typischeTage === null)
        ? null : +((vb - va) * typischeTage).toFixed(1);
    }
    raus.push({
      jahr: kandidat.jahr, abstand: +v.gesamt.toFixed(3),
      kennzahlAbstand: +v.d.toFixed(3),
      verlaufAbstand: v.verlauf === null ? null : +v.verlauf.toFixed(3),
      verlaufArt: v.verlauf === null ? null : quelle.art,
      fassung,
      stufe: mitStufen ? aehnlichkeitsStufe(v.gesamt, schwellen) : null,
      schwellen,
      werte: kandidat, unterschiede,
    });
  }

  /* Bei gleichem Abstand das jüngere Jahr zuerst — es liegt dem
     Nutzer näher und ist eher noch im Keller. */
  raus.sort((a, b) => a.abstand - b.abstand || b.jahr - a.jahr);
  return raus.slice(0, Math.max(0, anzahl));
}

/* Nachkommastellen der Unterschiede: Die Abweichungen je Tag sind
   klein (0,3 mm/Tag ist schon viel) und brauchen zwei Stellen. */
const STELLEN = { regenAbw: 2, sonneAbw: 2 };

function median(werte) {
  const s = [...werte].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}


/** Einordnung des Abstands — RELATIV zur eigenen Region.

    Die erste Fassung hatte feste Schwellen, kalibriert an einer
    einzigen Reihe über 13 Jahre. Das trägt nicht: Eine Region mit
    ausgeglichenem Klima hat durchweg kleine Abstände, eine mit
    Extremjahren durchweg große — und eine Reihe über 76 Jahre streut
    ganz anders als eine über 13. In Rheinhessen kam dabei für jeden
    Vorschlag „gering ähnlich" heraus, obwohl die nächsten Nachbarn
    dort selbstverständlich ähnlich sind.

    Deshalb wird eingestuft, wo ein Abstand INNERHALB dieser Region
    steht: Die nächsten 5 % aller Jahrespaare heißen „sehr ähnlich",
    die nächsten 15 % „gut vergleichbar", die nächsten 30 % „in Teilen
    ähnlich". Dieselbe Überlegung wie bei der z-Normierung der
    Merkmale — gemessen wird an der Region, nicht an einem
    Absolutmaßstab. ALLE Paare, nicht nur die zum Bezugsjahr — siehe
    `aehnlicheJahrgaenge`.

    `schwellen` kommt aus `aehnlicheJahrgaenge`; fehlt es, greift ein
    Rückfall auf die früheren festen Werte. */
function aehnlichkeitsStufe(d, schwellen = null) {
  if (d == null) return null;
  const s = schwellen || { sehr: 0.65, gut: 0.85, mittel: 1.10 };
  if (d < s.sehr) return "sehr";
  if (d < s.gut) return "gut";
  if (d < s.mittel) return "mittel";
  return "gering";
}


/** Quantile der Abstände einer Region — die Schwellen der Einstufung. */
function schwellenAus(abstaende) {
  if (!Array.isArray(abstaende) || abstaende.length < 6) return null;
  const sortiert = [...abstaende].sort((a, b) => a - b);
  const q = (p) => sortiert[Math.min(sortiert.length - 1, Math.floor(sortiert.length * p))];
  return { sehr: q(0.05), gut: q(0.15), mittel: q(0.30) };
}


export { KLIMA_FASSUNG, fehlendesJahr, letztesAbgeschlossenesJahr, reiheVorlaeufig, MERKMALE, MERKMALE_F4, MERKMALE_KALENDER, REIFEMONATE, VERLAUF_GEWICHT, VERLAUF_JE_FASSUNG, kuehleNaechte, abstand, aehnlicheJahrgaenge,
  aehnlichkeitsStufe, merkmalsFassung, reiheVeraltet, schwellenAus,
  formAbstand, formStreuungen, mitReifephase, streuungen, verlaufsForm, verlaufsQuelle };
